// Device Characterization (2D tabs): fit Delta_so, tv, gs and gv of the N = 1 Hamiltonian to measured energy-vs-field
// bands - the desktop's panel: orientation, algorithm, parameters (value / float / bounds), the band files with their
// branch, units and field ranges, a collective |B| range, the slope estimate, Run fit, and the fit plot.
import { ActionIcon, Alert, Button, Card, Checkbox, FileButton, Grid, Group, Menu, Paper, SegmentedControl, Select, Stack, Table,
  Text, TextInput, Tooltip } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { dataFigure, FitPlots, fitSummary, type FitResultJson } from "../components/DevicePlots";
import { ErrorBox, notifyError, notifyOk } from "../components/Errors";
import { Plot } from "../components/Plot";
import { latestRun, limitError, PreviousRuns, RunCard } from "../components/RunCard";
import { patch, post, uploadFile, type Job, type ProjectDetail } from "../lib/api";
import { checkCharacterize, estimateSlopes, fittedText, freshCharForm, inspectBandFile, newFileRow, withOrientation,
  type CharFile, type CharForm, type DeviceForms } from "../lib/device";
import { useLiveJob } from "../lib/hooks";

const KIND = "transport.characterize";

export function CharacterizePanel({ project, tab, forms, header, onApply }: {
  project: ProjectDetail; tab: string; forms: DeviceForms; header: ReactNode; onApply: (values: Record<string, string>) => void;
}) {
  const qc = useQueryClient();
  const F = forms.characterize;
  const saved = (project.settings?.characterize as Record<string, CharForm> | undefined)?.[tab];
  const [form, setForm] = useState<CharForm>(() => saved ?? freshCharForm(forms, tab));
  const [debounced] = useDebouncedValue(form, 300);
  const check = useQuery({
    queryKey: ["characterize-check", debounced],
    queryFn: () => checkCharacterize(debounced),
    placeholderData: (prev) => prev,
  });
  const c = check.data;

  const latest = latestRun(project, KIND);
  const [jobId, setJobId] = useState<string | undefined>(latest?.id);
  useEffect(() => { if (!jobId && latest) setJobId(latest.id); }, [latest, jobId]);
  const { job } = useLiveJob(jobId);
  const busy = job && (job.status === "queued" || job.status === "running");
  const result = job?.status === "succeeded" ? (job.result as FitResultJson & { tab: string } | undefined) : undefined;
  const [view, setView] = useState<"fit" | "data">("fit");

  // a fit started here copies its values back into the form when it finishes (the desktop's apply_result)
  const started = useRef<string | null>(null);
  useEffect(() => {
    if (result && job && started.current === job.id) {
      started.current = null;
      setForm((f) => ({ ...f, values: { ...f.values, ...fittedText(result.params) } }));
    }
  }, [result, job]);

  const set = (patchForm: Partial<CharForm>) => setForm((f) => ({ ...f, ...patchForm }));
  const setIn = (field: "values" | "bounds_min" | "bounds_max", name: string, v: string) =>
    setForm((f) => ({ ...f, [field]: { ...f[field], [name]: v } }));
  const setFile = (i: number, p: Partial<CharFile>) =>
    setForm((f) => ({ ...f, files: f.files.map((x, j) => (j === i ? { ...x, ...p } : x)) }));

  const addFile = async (id: string, name: string) => {
    const info = await inspectBandFile(id).catch(() => undefined);
    setForm((f) => ({ ...f, files: [...f.files, newFileRow(f, id, name, info?.ok ? info : undefined)] }));
  };
  const [uploading, setUploading] = useState(false);
  const uploadAndAdd = async (files: File[]) => {
    setUploading(true);
    try {
      for (const file of files) {
        const stored = await uploadFile(project.id, file);
        await addFile(stored.id, stored.name);
      }
    } catch (e) {
      notifyError(e);
    } finally {
      setUploading(false);
      qc.invalidateQueries({ queryKey: ["project", project.id] });
      qc.invalidateQueries({ queryKey: ["me"] });
    }
  };
  const uploads = project.files.filter((f) => f.kind === "input" && f.status === "ready");

  const estimate = useMutation({
    mutationFn: () => estimateSlopes(form),
    onSuccess: (r) => {
      if (!r.ok) return notifyError(new Error(r.error));
      if (r.values) set({ values: r.values });
      const found = (["gs", "gv"] as const).filter((k) => r[k] != null).map((k) => `${k} = ${r.values?.[k]}`).join(", ");
      (found ? notifyOk : (m: string) => notifyError(new Error(m)))(
        `Slope estimate - ${found ? `found ${found}` : "could not separate gs/gv from this data"}. ${r.message ?? ""}`);
    },
    onError: (e) => notifyError(e),
  });

  const run = useMutation({
    mutationFn: async () => {
      const fresh = await checkCharacterize(form);                      // never run on a stale check
      if (!fresh.ok) throw new Error(fresh.general_error || "Fix the fields marked in red first.");
      return post<Job>("/jobs", { kind: KIND, project_id: project.id, params: form });
    },
    onSuccess: (j) => {
      started.current = j.id;
      setJobId(j.id);
      setView("fit");
      qc.invalidateQueries({ queryKey: ["project", project.id] });
      qc.invalidateQueries({ queryKey: ["me"] });
      const all = (project.settings?.characterize as Record<string, CharForm> | undefined) ?? {};
      patch(`/projects/${project.id}`, { settings: { ...project.settings, characterize: { ...all, [tab]: form },
        transport_view: { tab, mode: "characterize" } } })
        .then(() => qc.invalidateQueries({ queryKey: ["project", project.id] })).catch(() => undefined);
    },
  });

  const shown = view === "fit" && result ? "fit" : "data";
  const hasData = !!c?.datasets.length;
  return (
    <Grid gutter="md">
      <Grid.Col span={{ base: 12, lg: 5 }}>
        <Card withBorder>
          {header}
          <Text size="sm" fs="italic" c="dimmed" mt="sm">
            Fit Delta_so, tv, gs and gv of the N = 1 Hamiltonian (eq 1 / Table S1) to one or more measured energy-vs-field
            bands. An out-of-plane sweep mainly constrains gs and gv; an in-plane sweep mainly constrains Delta_so and tv.
          </Text>
          <Group grow mt="sm" align="flex-start">
            <Select size="xs" label="Field orientation" data={F.orientations} value={form.orientation} allowDeselect={false}
              onChange={(v) => v && setForm((f) => withOrientation(f, v, forms))} />
            <Select size="xs" label="Fit algorithm" data={F.algorithms} value={form.algorithm} allowDeselect={false}
              onChange={(v) => v && set({ algorithm: v })} />
          </Group>

          <Text fw={600} size="sm" mt="md">Parameters</Text>
          <Text size="xs" c="dimmed">Value = fit start, or held fixed if not floated; bounds apply while floated.</Text>
          <Table verticalSpacing={4} horizontalSpacing={6} mt={4}>
            <Table.Thead>
              <Table.Tr><Table.Th /><Table.Th>Value</Table.Th><Table.Th>Float</Table.Th><Table.Th>Bounds</Table.Th></Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {F.params.map((p) => (
                <Table.Tr key={p.name}>
                  <Table.Td><Text size="sm">{p.label}</Text></Table.Td>
                  <Table.Td>
                    <TextInput size="xs" w={110} aria-label={p.label} value={form.values[p.name] ?? ""}
                      error={c?.param_errors[p.name]} onChange={(e) => setIn("values", p.name, e.currentTarget.value)} />
                  </Table.Td>
                  <Table.Td>
                    <Checkbox aria-label={`Float ${p.label}`} checked={!!form.floating[p.name]}
                      onChange={(e) => { const v = e.currentTarget.checked; setForm((f) => ({ ...f, floating: { ...f.floating, [p.name]: v } })); }} />
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      <TextInput size="xs" w={70} aria-label={`${p.label} minimum`} value={form.bounds_min[p.name] ?? ""}
                        onChange={(e) => setIn("bounds_min", p.name, e.currentTarget.value)} />
                      <Text size="xs">to</Text>
                      <TextInput size="xs" w={70} aria-label={`${p.label} maximum`} value={form.bounds_max[p.name] ?? ""}
                        onChange={(e) => setIn("bounds_max", p.name, e.currentTarget.value)} />
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          <Group justify="space-between" mt="md">
            <Text fw={600} size="sm">Measured bands</Text>
            <Group gap="xs">
              <Menu position="bottom-end" withinPortal>
                <Menu.Target><Button size="xs" variant="default" disabled={!uploads.length}>Add from project</Button></Menu.Target>
                <Menu.Dropdown mah={300} style={{ overflowY: "auto" }}>
                  {uploads.map((f) => (
                    <Menu.Item key={f.id} onClick={() => addFile(f.id, f.name)}>{f.name}</Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>
              <FileButton onChange={(files) => files.length && uploadAndAdd(files)} multiple accept=".txt,.dat,.csv,.tsv,text/plain,text/csv">
                {(props) => <Button size="xs" {...props} loading={uploading}>Upload band file(s)…</Button>}
              </FileButton>
            </Group>
          </Group>
          <Text size="xs" c="dimmed">
            Two columns, field and energy (spaces or commas), one optional header line; units are guessed from the header.
          </Text>
          <Stack gap="xs" mt="xs">
            {form.files.map((f, i) => (
              <FileRow key={i} file={f} error={c?.file_errors[String(i)]} forms={forms}
                onChange={(p) => setFile(i, p)} onRemove={() => setForm((x) => ({ ...x, files: x.files.filter((_, j) => j !== i) }))} />
            ))}
            {!form.files.length && <Text size="sm" c="dimmed">No band files yet.</Text>}
          </Stack>

          <Text fw={600} size="sm" mt="md">Collective |B| range</Text>
          <Group gap={6} align="center" wrap="wrap">
            <Text size="sm">Use only |B| between</Text>
            <TextInput size="xs" w={70} aria-label="Collective minimum |B|" value={form.collective_b_min}
              onChange={(e) => set({ collective_b_min: e.currentTarget.value })} />
            <Text size="sm">and</Text>
            <Tooltip label="Blank = no upper limit.">
              <TextInput size="xs" w={70} aria-label="Collective maximum |B|" value={form.collective_b_max}
                onChange={(e) => set({ collective_b_max: e.currentTarget.value })} />
            </Tooltip>
            <Text size="sm">T, for every dataset (on top of each file's own range).</Text>
          </Group>
          <Tooltip multiline w={320} label="Out-of-plane only: reads gs and/or gv off the large-|B| straight-line slope of the bands above (restricted to this range), refining the gs/gv values instead of running the nonlinear fit.">
            <Button size="xs" variant="light" mt="xs" onClick={() => estimate.mutate()} loading={estimate.isPending}
              disabled={form.orientation !== "perp" || !form.files.length}>
              Estimate gs/gv from large-|B| slopes
            </Button>
          </Tooltip>

          {c?.general_error && <Alert color="red" mt="sm" p="xs"><Text size="sm">{c.general_error}</Text></Alert>}
          {c?.limit && <ErrorBox error={limitError(c.limit)} />}
          <ErrorBox error={run.error} />
          <Group mt="md" justify="space-between">
            <Button variant="subtle" size="xs" onClick={() => setForm(freshCharForm(forms, tab))}>Reset (clear files and start over)</Button>
            <Button onClick={() => run.mutate()} loading={run.isPending} disabled={!c?.ok || !!c?.limit || !!busy}>
              {busy ? "Fitting…" : "Run fit"}
            </Button>
          </Group>
        </Card>
      </Grid.Col>

      <Grid.Col span={{ base: 12, lg: 7 }}>
        <RunCard job={job} exportName={`characterize_${result?.tab ?? tab}_export.zip`}
          empty={hasData ? <DataPlot check={c!} /> : <Text c="dimmed">Add measured bands and press Run fit. The fit appears here.</Text>}
          extra={result && result.tab === tab && (
            <Button size="xs" variant="default" onClick={() => onApply(fittedText(result.params))}>
              Apply fitted values to Energy Bands / Transport
            </Button>
          )}>
          {(result || hasData) && (
            <SegmentedControl size="xs" value={shown} onChange={(v) => setView(v as "fit" | "data")}
              data={[{ value: "fit", label: "Fit", disabled: !result }, { value: "data", label: "Measured data", disabled: !hasData }]} />
          )}
          {result && shown === "fit" && (
            <>
              <Text size="sm">{fitSummary(result)}</Text>
              <Text size="xs" c="dimmed">{result.algorithm_label}: {result.message} ({result.n_iter} iterations)</Text>
              <FitPlots result={result} />
            </>
          )}
          {shown === "data" && hasData && <DataPlot check={c!} />}
        </RunCard>
        <PreviousRuns project={project} kind={KIND} current={jobId} onOpen={setJobId}
          describe={(p) => `${String(p.tab ?? "").toUpperCase()} · ${(p.files ?? []).length} band file(s) · ${p.orientation === "par" ? "in-plane" : "out-of-plane"} · ${p.algorithm}`} />
      </Grid.Col>
    </Grid>
  );
}

function DataPlot({ check }: { check: { datasets: Parameters<typeof dataFigure>[0] } }) {
  const fig = dataFigure(check.datasets);
  return <Plot data={fig.traces} layout={fig.layout} height={460} />;
}

function FileRow({ file, error, forms, onChange, onRemove }: {
  file: CharFile; error?: string; forms: DeviceForms; onChange: (p: Partial<CharFile>) => void; onRemove: () => void;
}) {
  const F = forms.characterize;
  return (
    <Paper withBorder p="xs">
      <Group justify="space-between" wrap="nowrap">
        <Text size="sm" fw={500} truncate>{file.name}</Text>
        <ActionIcon size="sm" variant="subtle" color="red" aria-label={`Remove ${file.name}`} onClick={onRemove}>✕</ActionIcon>
      </Group>
      {error && <Text size="xs" c="red">{error}</Text>}
      <Group gap="xs" mt={4} wrap="wrap" align="flex-end">
        <Select size="xs" w={210} label="Level / gap" data={F.branches} value={file.branch} allowDeselect={false}
          onChange={(v) => v && onChange({ branch: v })} />
        <Select size="xs" w={70} label="B unit" data={F.field_units} value={file.field_unit} allowDeselect={false}
          onChange={(v) => v && onChange({ field_unit: v })} />
        <Select size="xs" w={75} label="E unit" data={F.energy_units} value={file.energy_unit} allowDeselect={false}
          onChange={(v) => v && onChange({ energy_unit: v })} />
        <TextInput size="xs" w={60} label="Weight" value={file.weight} onChange={(e) => onChange({ weight: e.currentTarget.value })} />
      </Group>
      <Group gap="xs" mt={6} wrap="wrap" align="center">
        <Tooltip multiline w={300} label="The data is E(B) − E(B = 0), not the absolute level or gap.">
          <Checkbox size="xs" label="Relative to B = 0" checked={file.relative} onChange={(e) => onChange({ relative: e.currentTarget.checked })} />
        </Tooltip>
        <Text size="xs">|B| from</Text>
        <TextInput size="xs" w={60} aria-label={`${file.name} minimum |B|`} value={file.b_min} onChange={(e) => onChange({ b_min: e.currentTarget.value })} />
        <Text size="xs">to</Text>
        <TextInput size="xs" w={60} aria-label={`${file.name} maximum |B|`} placeholder="no limit" value={file.b_max}
          onChange={(e) => onChange({ b_max: e.currentTarget.value })} />
        <Text size="xs">T</Text>
        <Select size="xs" w={130} aria-label={`${file.name} field sign`} data={F.b_signs} value={file.b_sign} allowDeselect={false}
          onChange={(v) => v && onChange({ b_sign: v })} />
      </Group>
    </Paper>
  );
}
